import type {PermissionType} from '@hconnect/apiclient'
import {useUpmPlant} from '@hconnect/upm'
import {usePermissionsParsedQuery, checkPermissionsByDataScope} from '@hproduce/permissions'
import {checkPermissionAccessByDataScope} from '@hproduce/permissions/src/helpers'
import {Navigate} from 'react-router'

import {ROUTES} from '../../routing/routes'

import {useUrlParam} from '@refra/modules/routing/hooks'

interface PermissionGuardWrapperProps {
  requiredPermissionTypes: PermissionType[]
  operator?: 'AND' | 'OR'
  children: React.ReactElement
}

export const PermissionsValidationWrapper: React.FC<PermissionGuardWrapperProps> = ({
  requiredPermissionTypes,
  operator = 'AND',
  children
}) => {
  const plantId = useUrlParam('plantCode')
  const {country} = useUpmPlant({plantId})

  const {data: permissions} = usePermissionsParsedQuery(requiredPermissionTypes)
  if (permissions === undefined) {
    throw new Error('Permissions data must be loaded beforehand')
  }

  const handleAndOperator = () => {
    return checkPermissionsByDataScope(
      permissions,
      {plantId, countryId: country},
      requiredPermissionTypes
    )
  }

  const handleOrOperator = () => {
    return requiredPermissionTypes
      .map((permissionType) =>
        permissions.some(
          (permission) =>
            permission.permissionType === permissionType &&
            checkPermissionAccessByDataScope(country, plantId, permission.parsedDataScope)
        )
      )
      .some((isGranted) => isGranted)
  }

  const isAccessGranted = operator === 'AND' ? handleAndOperator() : handleOrOperator()

  if (!isAccessGranted) {
    return <Navigate to={ROUTES.NOT_AUTHORIZED.path} replace />
  }

  return children
}
